import { useState } from 'react';
import {
  ClipboardList, CreditCard, Inbox, Package, ReceiptText, RotateCcw, ShoppingCart, Users,
} from 'lucide-react';
import { api, unwrapPaged } from '@/lib/api';
import { useLocale } from '@/i18n/locale-provider';
import { useAsync } from '@/lib/use-async';
import {
  Badge, Card, EmptyState, ErrorPanel, PageHeader, Spinner, Table, Td, statusTone,
} from '@/components/ui';

/**
 * The record-browsing screens.
 *
 * They share one shell because they are the same interaction — filter, page, inspect —
 * over different collections. Each supplies its columns and how to render a row; nothing
 * else differs, so nothing else is duplicated.
 */
interface ListPageProps<T> {
  title: string;
  subtitle: string;
  icon: typeof Package;
  /** Which client and path to read from. */
  load: (query: string) => Promise<{ items: T[]; total: number; totalPages: number }>;
  columns: string[];
  row: (item: T) => React.ReactNode;
  filters?: Array<{ key: string; label: string; options: Array<{ value: string; label: string }> }>;
  searchable?: boolean;
}

function ListPage<T extends { id?: string }>({
  title, subtitle, icon, load, columns, row, filters = [], searchable = true,
}: ListPageProps<T>) {
  const { t, n, fill } = useLocale();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [active, setActive] = useState<Record<string, string>>({});

  const query = new URLSearchParams({ page: String(page), pageSize: '25' });
  if (applied) query.set('search', applied);
  for (const [key, value] of Object.entries(active)) if (value) query.set(key, value);

  const result = useAsync(() => load(query.toString()), [query.toString()]);

  return (
    <>
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={
          result.data
            ? <Badge tone="neutral">{fill(t.common.records, { count: n(result.data.total) })}</Badge>
            : undefined
        }
      />

      {(searchable || filters.length > 0) && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {searchable && (
            <form
              className="flex gap-2"
              onSubmit={(event) => { event.preventDefault(); setApplied(search); setPage(1); }}
            >
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t.common.search}
                aria-label={`${t.common.search} ${title}`}
                className="input !w-56 !py-1.5"
              />
              <button type="submit" className="btn-secondary !py-1.5">{t.common.search.replace('…', '')}</button>
            </form>
          )}
          {filters.map((filter) => (
            <select
              key={filter.key}
              value={active[filter.key] ?? ''}
              onChange={(event) => {
                setActive({ ...active, [filter.key]: event.target.value });
                setPage(1);
              }}
              aria-label={filter.label}
              className="input !w-auto !py-1.5"
            >
              <option value="">{filter.label}: {t.common.all}</option>
              {filter.options.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          ))}
        </div>
      )}

      {result.error ? (
        <ErrorPanel error={result.error} onRetry={result.reload} title={t.common.errorTitle} />
      ) : result.loading && !result.data ? (
        <Spinner />
      ) : !result.data?.items.length ? (
        <EmptyState icon={icon} title={fill(t.common.noResults, { entity: title })}
          detail={t.common.adjustFilters} />
      ) : (
        <Card>
          <Table head={columns}>
            {result.data.items.map((item, index) => (
              <tr key={item.id ?? index} className="hover:bg-mist-50">{row(item)}</tr>
            ))}
          </Table>

          {result.data.totalPages > 1 && (
            <nav aria-label={t.common.pageOf} className="mt-4 flex items-center justify-between border-t border-mist-200 pt-4">
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="btn-secondary !py-1.5">
                {t.common.previous}
              </button>
              <span className="tabular text-sm text-mist-600">
                {fill(t.common.pageOf, { page: n(page), total: n(result.data.totalPages) })}
              </span>
              <button
                type="button"
                disabled={page >= result.data.totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="btn-secondary !py-1.5"
              >
                {t.common.next}
              </button>
            </nav>
          )}
        </Card>
      )}
    </>
  );
}

/* ---------------------------------------------------------------- products */

export function ProductsPage() {
  const { t, n, moneyExact, field } = useLocale();
  return (
    <ListPage
      title={t.records.products}
      subtitle={t.records.productsSubtitle}
      icon={Package}
      load={(query) => unwrapPaged<never>(api.simulator.get(`/products?${query}`))}
      columns={[t.records.colProduct, t.records.colCategory, t.records.colPrice, t.records.colStock, t.records.colStatus]}
      row={(p: never) => {
        const product = p as unknown as {
          name: string; nameAr: string | null; sku: string; brand: string | null; basePrice: number;
          category?: { name: string; nameAr: string | null };
          stockAvailable: number; inStock: boolean; isActive: boolean;
        };
        return (
          <>
            <Td>
              <p className="font-medium text-mist-800">{field(product, 'name')}</p>
              <p className="font-mono text-xs text-mist-500">
                <span className="ltr-embed">{product.sku}</span>{product.brand && ` · ${product.brand}`}
              </p>
            </Td>
            <Td className="text-mist-600">{field(product.category, 'name') || '—'}</Td>
            <Td className="tabular text-end text-mist-800">{moneyExact(product.basePrice)}</Td>
            <Td className="tabular text-end">
              <span className={product.stockAvailable <= 10 ? 'font-semibold text-warning-fg' : 'text-mist-700'}>
                {n(product.stockAvailable)}
              </span>
            </Td>
            <Td><Badge tone={product.isActive ? 'good' : 'neutral'}>{product.isActive ? t.records.active : t.records.inactive}</Badge></Td>
          </>
        );
      }}
    />
  );
}

/* --------------------------------------------------------------- customers */

export function CustomersPage() {
  const { t, dateTime, field, personName } = useLocale();
  return (
    <ListPage
      title={t.records.customers}
      subtitle={t.records.customersSubtitle}
      icon={Users}
      load={(query) => unwrapPaged<never>(api.simulator.get(`/customers?${query}`))}
      columns={[t.records.colCustomer, t.records.colType, t.records.colCity, t.records.colPersona, t.records.colCreated]}
      filters={[{
        key: 'customerType', label: t.records.filterType,
        options: ['RETAIL', 'VIP', 'WHOLESALE', 'CORPORATE'].map((v) => ({ value: v, label: v })),
      }]}
      row={(c: never) => {
        const customer = c as unknown as {
          firstName: string; lastName: string;
          firstNameAr: string | null; lastNameAr: string | null;
          email: string; customerType: string;
          city: string | null; cityAr: string | null;
          personaCode: string | null; isSynthetic: boolean; createdAt: string;
        };
        return (
          <>
            <Td>
              <p className="font-medium text-mist-800">
                {personName(customer)}
                {customer.isSynthetic && <Badge tone="info">{t.records.synthetic}</Badge>}
              </p>
              <p className="ltr-embed text-xs text-mist-500">{customer.email}</p>
            </Td>
            <Td><Badge tone={customer.customerType === 'VIP' ? 'good' : 'neutral'}>{customer.customerType}</Badge></Td>
            <Td className="text-mist-600">{field(customer, 'city') || '—'}</Td>
            <Td className="ltr-embed text-xs text-glow-700">{customer.personaCode ?? '—'}</Td>
            <Td className="text-xs text-mist-500">{dateTime(customer.createdAt)}</Td>
          </>
        );
      }}
    />
  );
}

/* ------------------------------------------------------------------ orders */

export function OrdersPage() {
  const { t, moneyExact, dateTime, field, personName } = useLocale();
  return (
    <ListPage
      title={t.records.orders}
      subtitle={t.records.ordersSubtitle}
      icon={ShoppingCart}
      load={(query) => unwrapPaged<never>(api.simulator.get(`/orders?${query}`))}
      columns={[t.records.colOrder, t.records.colCustomer, t.records.colChannel, t.records.colStatus,
                t.records.colTotal, t.records.colPlaced]}
      filters={[
        {
          key: 'status', label: t.records.filterStatus,
          options: ['PENDING', 'AWAITING_PAYMENT', 'CONFIRMED', 'FULFILLED', 'CANCELLED', 'PAYMENT_FAILED']
            .map((v) => ({ value: v, label: v })),
        },
        {
          key: 'channel', label: t.records.filterChannel,
          options: ['ONLINE', 'PHYSICAL_BRANCH', 'MOBILE', 'AGENT_SIMULATION'].map((v) => ({ value: v, label: v })),
        },
      ]}
      row={(o: never) => {
        const order = o as unknown as {
          orderNumber: string; status: string; channel: string; grandTotal: number;
          createdAt: string; simulationRunId: string | null;
          customer?: { firstName: string; lastName: string; firstNameAr: string | null; lastNameAr: string | null };
          branch?: { name: string; nameAr: string | null } | null;
        };
        return (
          <>
            <Td>
              <p className="ltr-embed font-mono text-xs text-deep-600">{order.orderNumber}</p>
              {order.simulationRunId && <p className="mt-0.5 text-[10px] text-mist-400">{t.records.agentGenerated}</p>}
            </Td>
            <Td className="text-mist-700">
              {order.customer ? personName(order.customer) : '—'}
            </Td>
            <Td className="text-xs text-mist-600">
              {order.channel.replace(/_/g, ' ').toLowerCase()}
              {order.branch && <span className="block text-mist-400">{field(order.branch, 'name')}</span>}
            </Td>
            <Td><Badge tone={statusTone(order.status)}>{order.status}</Badge></Td>
            <Td className="tabular text-end font-medium text-deep-600">{moneyExact(order.grandTotal)}</Td>
            <Td className="text-xs text-mist-500">{dateTime(order.createdAt)}</Td>
          </>
        );
      }}
    />
  );
}

/* ---------------------------------------------------------------- invoices */

export function InvoicesPage() {
  const { t, moneyExact, dateTime, personName } = useLocale();
  return (
    <ListPage
      title={t.records.invoices}
      subtitle={t.records.invoicesSubtitle}
      icon={ReceiptText}
      load={(query) => unwrapPaged<never>(api.simulator.get(`/invoices?${query}`))}
      columns={[t.records.colInvoice, t.records.colCustomer, t.records.colChannel, t.records.colStatus,
                t.records.colTotal, t.records.colIssued]}
      filters={[{
        key: 'status', label: t.records.filterStatus,
        options: ['ISSUED', 'PAID', 'CANCELLED', 'CREDITED'].map((v) => ({ value: v, label: v })),
      }]}
      row={(i: never) => {
        const invoice = i as unknown as {
          invoiceNumber: string; status: string; sourceChannel: string; grandTotal: number;
          issuedAt: string;
          customer?: { firstName: string; lastName: string; firstNameAr: string | null; lastNameAr: string | null };
        };
        return (
          <>
            <Td><span className="ltr-embed font-mono text-xs text-deep-600">{invoice.invoiceNumber}</span></Td>
            <Td className="text-mist-700">
              {invoice.customer ? personName(invoice.customer) : '—'}
            </Td>
            <Td className="text-xs text-mist-600">{invoice.sourceChannel.replace(/_/g, ' ').toLowerCase()}</Td>
            <Td><Badge tone={statusTone(invoice.status)}>{invoice.status}</Badge></Td>
            <Td className="tabular text-end font-medium text-deep-600">{moneyExact(invoice.grandTotal)}</Td>
            <Td className="text-xs text-mist-500">{dateTime(invoice.issuedAt)}</Td>
          </>
        );
      }}
    />
  );
}

/* ---------------------------------------------------------------- payments */

export function PaymentsPage() {
  const { t, moneyExact, dateTime } = useLocale();
  return (
    <ListPage
      title={t.records.payments}
      subtitle={t.records.paymentsSubtitle}
      icon={CreditCard}
      searchable={false}
      load={(query) => unwrapPaged<never>(api.simulator.get(`/payments?${query}`))}
      columns={[t.records.colReference, t.records.colMethod, t.records.colStatus, t.records.colAmount,
                t.records.colRefunded, t.records.colWhen]}
      filters={[
        {
          key: 'status', label: t.records.filterStatus,
          options: ['PENDING', 'COMPLETED', 'FAILED', 'REFUNDED', 'PARTIALLY_REFUNDED']
            .map((v) => ({ value: v, label: v })),
        },
        {
          key: 'method', label: t.records.filterMethod,
          options: ['CARD', 'CASH', 'BANK_TRANSFER', 'WALLET', 'COD'].map((v) => ({ value: v, label: v })),
        },
      ]}
      row={(p: never) => {
        const payment = p as unknown as {
          transactionReference: string; paymentMethod: string; status: string;
          amount: number; refundedAmount: number; failureReason: string | null; createdAt: string;
        };
        return (
          <>
            <Td><span className="ltr-embed font-mono text-xs text-mist-600">{payment.transactionReference}</span></Td>
            <Td className="text-mist-700">{payment.paymentMethod}</Td>
            <Td>
              <Badge tone={statusTone(payment.status)}>{payment.status}</Badge>
              {payment.failureReason && (
                <p className="mt-0.5 max-w-[16rem] truncate text-xs text-danger-fg" title={payment.failureReason}>
                  {payment.failureReason}
                </p>
              )}
            </Td>
            <Td className="tabular text-end font-medium text-deep-600">{moneyExact(payment.amount)}</Td>
            <Td className="tabular text-end text-mist-500">
              {payment.refundedAmount > 0 ? moneyExact(payment.refundedAmount) : '—'}
            </Td>
            <Td className="text-xs text-mist-500">{dateTime(payment.createdAt)}</Td>
          </>
        );
      }}
    />
  );
}

/* ----------------------------------------------------------------- returns */

export function ReturnsPage() {
  const { t, moneyExact, dateTime } = useLocale();
  return (
    <ListPage
      title={t.records.returns}
      subtitle={t.records.returnsSubtitle}
      icon={RotateCcw}
      searchable={false}
      load={(query) => unwrapPaged<never>(api.simulator.get(`/returns?${query}`))}
      columns={[t.records.colReturn, t.records.colReason, t.records.colStatus, t.records.colRefund,
                t.records.colRaised]}
      filters={[{
        key: 'status', label: t.records.filterStatus,
        options: ['REQUESTED', 'APPROVED', 'REJECTED', 'REFUNDED'].map((v) => ({ value: v, label: v })),
      }]}
      row={(r: never) => {
        const record = r as unknown as {
          returnNumber: string; reason: string; status: string; refundAmount: number; createdAt: string;
        };
        return (
          <>
            <Td><span className="ltr-embed font-mono text-xs text-deep-600">{record.returnNumber}</span></Td>
            <Td className="text-mist-700">{record.reason}</Td>
            <Td><Badge tone={statusTone(record.status)}>{record.status}</Badge></Td>
            <Td className="tabular text-end font-medium text-deep-600">{moneyExact(record.refundAmount)}</Td>
            <Td className="text-xs text-mist-500">{dateTime(record.createdAt)}</Td>
          </>
        );
      }}
    />
  );
}

/* ------------------------------------------------------------------- users */

export function UsersPage() {
  const { t, dateTime } = useLocale();
  return (
    <ListPage
      title={t.records.users}
      subtitle={t.records.usersSubtitle}
      icon={Users}
      load={(query) => unwrapPaged<never>(api.auth.get(`/users?${query}`))}
      columns={[t.records.colUser, t.records.colRoles, t.records.colStatus, t.records.colLastSignIn]}
      row={(u: never) => {
        const user = u as unknown as {
          fullName: string; email: string; isActive: boolean; mustChangePassword: boolean;
          roles: Array<{ code: string; name: string }>; lastLoginAt: string | null;
        };
        return (
          <>
            <Td>
              <p className="font-medium text-mist-800">{user.fullName}</p>
              <p className="ltr-embed text-xs text-mist-500">{user.email}</p>
            </Td>
            <Td>
              <div className="flex flex-wrap gap-1">
                {user.roles.map((role) => <Badge key={role.code} tone="info">{role.name}</Badge>)}
              </div>
            </Td>
            <Td>
              <Badge tone={user.isActive ? 'good' : 'bad'}>{user.isActive ? t.records.active : t.records.disabled}</Badge>
              {user.mustChangePassword && <Badge tone="warn">{t.records.mustChangePassword}</Badge>}
            </Td>
            <Td className="text-xs text-mist-500">{dateTime(user.lastLoginAt)}</Td>
          </>
        );
      }}
    />
  );
}

/* -------------------------------------------------------------- audit logs */

export function AuditLogsPage() {
  const { t, dateTime } = useLocale();
  return (
    <ListPage
      title={t.records.audit}
      subtitle={t.records.auditSubtitle}
      icon={ClipboardList}
      searchable={false}
      load={(query) => unwrapPaged<never>(api.simulator.get(`/audit-logs?${query}`))}
      columns={[t.records.colAction, t.records.colEntity, t.records.colActor, t.records.colRequest,
                t.records.colWhen]}
      filters={[{
        key: 'entityType', label: t.records.filterEntity,
        options: ['Order', 'Payment', 'Invoice', 'Return', 'Customer'].map((v) => ({ value: v, label: v })),
      }]}
      row={(a: never) => {
        const log = a as unknown as {
          action: string; entityType: string; entityId: string; actorType: string | null;
          actorName: string | null; actorId: string | null; requestId: string | null;
          simulationRunId: string | null; createdAt: string;
        };
        return (
          <>
            <Td><span className="ltr-embed font-mono text-xs text-deep-600">{log.action}</span></Td>
            <Td>
              <p className="text-mist-700">{log.entityType}</p>
              <p className="ltr-embed font-mono text-[10px] text-mist-400">{log.entityId.slice(0, 8)}…</p>
            </Td>
            <Td className="text-xs text-mist-600">
              {log.actorName ?? log.actorId?.slice(0, 8) ?? t.records.system}
              {log.actorType && <span className="ms-1 text-mist-400">({log.actorType})</span>}
              {log.simulationRunId && <p className="text-[10px] text-glow-700">{t.records.agentRun}</p>}
            </Td>
            <Td className="ltr-embed font-mono text-[10px] text-mist-400">{log.requestId?.slice(0, 8) ?? '—'}</Td>
            <Td className="text-xs text-mist-500">{dateTime(log.createdAt)}</Td>
          </>
        );
      }}
    />
  );
}

export { Inbox };
